import _ from 'lodash';
import {
  Tranche,
  TrancheGroup
} from '../../gameday/data/gameDay-data.transformations';
import { GameDayViewType } from '../../state/data/state';
import { TrancheSummaryParams } from '../models/tranche-summary-params';
import { TrancheSummaryData } from '../models/tranche-summary-data';
import { GameDayModel } from '../../models/gameday-model';
import { CallFeature, CallType } from '@ipreo/muni-shared';

const NOT_RELEVANT_CALL_FEATURE: CallFeature = {
  securityType: undefined,
  displayText: 'Not optionally callable',
  type: CallType.notCallable
};

function getCallFeatures(tranche: Tranche): CallFeature[] {
  return tranche.maturities.map(
    maturity => maturity.callFeature || NOT_RELEVANT_CALL_FEATURE
  );
}

function getCallFeaturesText(callFeatures: CallFeature[]): string[] {
  return _(callFeatures)
    .map(callFeature => callFeature && callFeature.displayText)
    .filter(text => !!text)
    .uniq()
    .value();
}

function getInsurance({ insurer, series }: GameDayModel.Maturity): string {
  return insurer || series.insurer;
}

/**
 * Return mapped tranche summary params to tranche summary data.
 * Map tranche maturities insurer, series, yield prices, coupons from maturities
 *
 * @param params
 * @returns TrancheSummaryData
 */
export function trancheSummaryDataMapper(
  params: TrancheSummaryParams
): TrancheSummaryData {
  const { deal, tranche } = params;

  if (!tranche) {
    return undefined;
  }

  const date = tranche.maturities[0].maturityDate.toDate();
  const seriesIds: string[] = deal.series.map((s: GameDayModel.Series) => s.id);
  let series: string[] = [];
  let yieldPrices: number[] = [];
  let coupons: number[] = [];
  const insurers: string[] = [];
  let parAmount = 0;

  tranche.maturities.forEach(maturity => {
    const insurer = getInsurance(maturity);
    const seriesIndex = seriesIds.indexOf(maturity.seriesId);
    if (seriesIndex !== -1) {
      series.push(deal.series[seriesIndex].code);
    }
    if (insurer) {
      insurers.push(insurer);
    }
    if (maturity.yield) {
      yieldPrices.push(maturity.yield);
    }
    if (maturity.coupon || maturity.coupon === 0) {
      coupons.push(maturity.coupon);
    }
    parAmount += maturity.size;
  });
  coupons.sort((a, b) => a - b);
  //noinspection TypeScriptUnresolvedFunction
  coupons = _.sortedUniq(coupons);
  yieldPrices.sort((a, b) => a - b);
  //noinspection TypeScriptUnresolvedFunction
  yieldPrices = _.sortedUniq(yieldPrices);
  series = _.uniq(series);
  coupons =
    coupons.length > 1 ? [coupons[0], coupons[coupons.length - 1]] : coupons;
  yieldPrices =
    yieldPrices.length > 1
      ? [yieldPrices[0], yieldPrices[yieldPrices.length - 1]]
      : yieldPrices;

  const orders = tranche.orders.slice();

  let ordersAmount = orders.reduce((amount, order) => amount + order.amount, 0);
  const subscription = ordersAmount / parAmount;

  let balance = parAmount - ordersAmount;

  balance /= 1000;
  ordersAmount /= 1000;
  parAmount /= 1000;

  const callFeatures = getCallFeaturesText(getCallFeatures(tranche));
  return {
    date,
    series,
    parAmount,
    ordersAmount,
    subscription,
    balance,
    coupons,
    yieldPrices,
    insurers,
    callFeatures
  };
}

/**
 * Returns tranche summary params from issue, orders, tranche groups state
 * with combined tranche and view type if they are defined or default empty params.
 *
 * @param state
 * @param tranche
 * @param viewType
 * @returns TrancheSummaryParams
 */
export function trancheSummaryParamsMapper(
  {
    deal,
    orders,
    trancheGroups
  }: {
    deal: GameDayModel.Deal;
    orders: GameDayModel.Order[];
    trancheGroups: TrancheGroup[];
  },
  tranche: Tranche,
  viewType: GameDayViewType
): TrancheSummaryParams {
  return deal && orders && trancheGroups && tranche
    ? {
        deal,
        orders,
        trancheGroups,
        tranche,
        viewType
      }
    : undefined;
}
